<template>
  <div :class="[$b('wrapper'), columns === 1 && 'global-wrapper-large']">
    <section
      :class="[
        $b({ background, custom: customClass }),
        $b(`${columns}-column`),
        $b('column')
      ]"
      :style="backgroundCSSVar"
    >
      <div :class="$b('inner')">
        <img
          v-if="
            block.icon && block.icon.src && block.iconPosition == 'Above heading'
          "
          :src="block.icon.src"
          :alt="block.icon.alt"
          :class="$b('icon')"
          loading="lazy"
        />
        <div
          :class="$b('inner-container', { iconBelow: iconPosition === 'below' })"
        >
          <HighlightHeading :heading="block.heading[0]" />
          <div>
            <img
              v-if="
                block.icon &&
                  block.icon.src &&
                  block.iconPosition == 'Below heading'
              "
              :src="block.icon.src"
              :alt="block.icon.alt"
              :class="$b('icon')"
              loading="lazy"
            />
            <SustainabilitySubheading
              v-if="block.showSubheading"
              :content="block.subheading"
              :colour="block.subheadingColour"
            />
            <div :class="$b('text-link-container')">
              <component
                :is="linkType"
                size="regular"
                :invert="invertedLink"
                is-button
                :class="$b('text-link')"
                @click="openModal(modalSlug)"
              >
                {{ $tr('Learn More') }}
              </component>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { SecondaryLineLink, LineLink } from '#components'

export default {
  name: 'SustainabilityBlock',

  components: {
    SecondaryLineLink,
    LineLink
  },

  props: {
    block: {
      type: Object,
      required: true,
    },
    columns: {
      type: Number,
      default: 1,
    }
  },

  async setup(props) {
    const modalSlug = computed(() => {
      return props.block?.modalContent?.slug
        ? props.block.modalContent.slug
        : null
    })

    const modalStore = useModalStore()
    const route = useRoute()

    if (modalSlug.value && route.query.modal === modalSlug.value) {
      await modalStore.PREFETCH_CONTENT(modalSlug.value)
    }

    return { modalStore, modalSlug }
  },

  computed: {
    background() {
      return this.block.backgroundColour
    },
    customClass() {
      return this.block?.customClass
    },
    hasBackground() {
      return (
        this.block?.mobileBackground?.src || this.block?.desktopBackground?.src
      )
    },
    backgroundCSSVar() {
      if (
        this.block?.mobileBackground?.src &&
        this.block?.desktopBackground?.src
      )
        return `
          --mobileBg:url('${this.block.mobileBackground.src}');
          --desktopBg:url('${this.block.desktopBackground.src}');
        `
      else if (this.block?.desktopBackground?.src)
        return `
          --mobileBg:url('${this.block.desktopBackground.src}');
          --desktopBg:url('${this.block.desktopBackground.src}');
        `
      return null
    },
    linkType() {
      return this.background === 'grey' ? 'SecondaryLineLink' : 'LineLink'
    },
    invertedLink() {
      return this.background !== 'grey'
    },
    iconPosition() {
      return this.block.iconPosition === 'Above heading' ? 'above' : 'below'
    },
  },

  methods: {
    openModal(slug) {
      this.modalStore.OPEN_MODAL_PAGE({ slug })
    },
  },
}
</script>

<style lang="postcss">
.SustainabilityBlock__wrapper {
  @apply flex-grow h-full;
}

.SustainabilityBlock {
  @apply px-sm md:px-md
    flex-grow h-full
    relative
    bg-cover bg-no-repeat;
  border-radius: 10px;
  background-image: var(--mobileBg);

  @screen md {
    background-image: var(--desktopBg);
  }

  &--background-black {
    @apply bg-off-black text-white;
  }

  &--background-grey {
    @apply bg-grey-light text-off-black;
  }

  &--background-gradient {
    @apply text-white;
    background-color: #5c0f99;
    background-image: var(--gradient-default);
  }

  &.SustainabilityBlock--custom-bcorp {
    @apply pt-lg;

    @screen md {
      background-color: #5c0f99;
      background-image: var(--desktopBg), var(--gradient-default);
      background-size: 17.8% auto, 100% 100%;
      background-position: right 17% center, center;
    }

    .SustainabilityBlock__icon {
      @apply block md:hidden
        pb-md;
      width: 66px;

      @screen sm {
        width: 96px;
      }
    }

    .SustainabilityBlock__inner-container {
      @apply flex flex-col h-full justify-between
        md:pt-xl;

      @screen lg {
        padding-right: 35%;
      }
    }
  }

  &.SustainabilityBlock--custom-kings-award {
    @apply pt-lg;

    @screen md {
      background-color: #5c0f99;
      background-image: var(--desktopBg), var(--gradient-default);
      background-size: 17.8% auto, 100% 100%;
      background-position: right 17% center, center;
    }

    .SustainabilityBlock__icon {
      @apply block md:hidden
        pb-md;
      width: 66px;

      @screen sm {
        width: 96px;
      }
    }

    .SustainabilityBlock__inner-container {
      @apply md:pt-xl;

      @screen lg {
        padding-right: 35%;
      }
    }
  }

  &.SustainabilityBlock--custom-carbon-neutral {
    .SustainabilitySubheading__heading {
      @apply mdMax:pt-md;
    }
  }

  &.SustainabilityBlock--custom-one-pc-planet {
    @apply pt-lg;
    background-position: right theme('spacing.sm') bottom 60px;
    background-size: 38px auto;

    @screen sm {
      background-image: var(--desktopBg);
      background-size: 34% auto;
      background-position: right 13.7% center;
    }

    .SustainabilityBlock__icon {
      @apply block sm:hidden
        pb-md;
      width: 97px;
    }

    .SustainabilityBlock__inner-container {
      @apply flex flex-col h-full justify-between
        md:pt-xl;
    }
  }

  &.SustainabilityBlock--custom-built-to-last {
    @apply bg-contain;
    background-origin: border-box;
    background-position: right center;
    background-blend-mode: lighten;

    @screen md {
      background-size: 63% auto;
      background-position: right 60px center;
    }
  }

  &.SustainabilityBlock--custom-infinite {
    .SustainabilityBlock__icon {
      width: 60px;

      @screen md {
        width: 97px;
      }
    }

    .HighlightHeading {
      @apply pb-xxs md:pb-xs;
    }
  }

  &.SustainabilityBlock--custom-sas {
    .SustainabilityBlock__icon {
      @apply pb-xs md:pb-sm;
      width: 108px;

      @screen md {
        width: 159px;
      }
    }
  }

  &.SustainabilityBlock--custom-bike-project {
    background-origin: border-box;
    background-position: right top 200px;
    background-size: 215px auto;

    @screen md {
      background-size: 272px auto;
      background-position: right top 260px;
    }
  }
}

.SustainabilityBlock__column {
  @apply pb-md md:pb-lg;
}

.SustainabilityBlock__1-column {
  @apply pt-xl md:pt-xxl;
}

.SustainabilityBlock__2-column {
  @apply pt-xl md:pt-xl;
}

.SustainabilityBlock__inner {
  @apply flex flex-col justify-between
    h-full;
}

.SustainabilityBlock__inner-container--icon-below {
  @apply flex flex-col h-full justify-between;
}

.SustainabilityBlock__text-link-container {
  @apply pt-md mt-xs mb-xs md:mb-sm;
}
</style>
